// O MODELO (template) DO WHATSAPP OFICIAL — a conta, sem tela (25/09/2026).
//
// Tudo o que a tela de Configurações › Modelos do WhatsApp precisa saber de um
// modelo e que não depende de desenho: quais variáveis ele tem, o que a Meta
// vai recusar, como ele chega no celular do cliente e o corpo que vai para a
// análise. A tela só mostra.
//
// As regras vêm da documentação da Meta e da WAME (a parceira pela qual a A&T
// usa a API oficial). Checar ANTES é o que a tela da WAME não faz: lá a recusa
// só chega depois, em inglês, e o nome já ficou gasto.

export const LIMITES = {
  corpo: 1024,
  cabecalho: 60,
  rodape: 60,
  botao: 25,
  botoes: 3,
  emojis: 10,
};

export const CATEGORIAS = ['UTILITY', 'MARKETING', 'AUTHENTICATION'];
export const TIPOS_DE_CABECALHO = [
  'NONE',
  'TEXT',
  'IMAGE',
  'VIDEO',
  'DOCUMENT',
];
export const TIPOS_DE_BOTAO = ['QUICK_REPLY', 'URL', 'PHONE_NUMBER'];
// O que o seletor de arquivo aceita para cada tipo de cabeçalho (o que a Meta aceita).
export const ARQUIVOS_DO_CABECALHO = {
  IMAGE: 'image/jpeg,image/png',
  VIDEO: 'video/mp4',
  DOCUMENT: 'application/pdf',
};

// Os status que a Meta devolve, na ordem em que a lista agrupa.
export const STATUS = [
  'APPROVED',
  'PENDING',
  'IN_APPEAL',
  'REJECTED',
  'PAUSED',
  'DISABLED',
];

const VARIAVEL = /\{\{\s*(\d+)\s*\}\}/g;
const ENCURTADORES =
  /\b(bit\.ly|tinyurl\.com|goo\.gl|t\.co|ow\.ly|is\.gd|cutt\.ly|encurtador\.com\.br|rebrand\.ly)\b/i;
// Promessa é o que a OAB proíbe e o que a Meta lê como promoção.
const PROMESSA =
  /\b(garantid[oa]s?|garantimos|100\s?%|ganhe|gr[aá]tis|imperd[ií]vel|[uú]ltima chance|promo[cç][aã]o)\b/i;
const EMOJI = /\p{Extended_Pictographic}/gu;

export const modeloVazio = () => ({
  id: null,
  categoria: 'UTILITY',
  idioma: 'pt_BR',
  nome: '',
  cabecalho: { tipo: 'NONE', texto: '', exemplo: '', arquivo: null },
  corpo: '',
  exemplos: [],
  rodape: '',
  botoes: [],
});

// "Lembrete de audiência" → "lembrete_de_audiencia": a Meta só aceita
// minúsculas, números e sublinhado.
export function nomeTecnico(texto = '') {
  return texto
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 512);
}

// "lembrete_de_audiencia" → "Lembrete de audiencia": o nome na lista, para
// quem lê, sem sublinhado.
export function nomeLegivel(nome = '') {
  const t = nome.replace(/_/g, ' ').trim();
  return t ? t[0].toUpperCase() + t.slice(1) : '';
}

// Os números das variáveis, na ordem em que aparecem, sem repetir.
export function variaveisDe(texto = '') {
  const vistos = [];
  [...texto.matchAll(VARIAVEL)].forEach(m => {
    const n = Number(m[1]);
    if (!vistos.includes(n)) vistos.push(n);
  });
  return vistos;
}

// A próxima variável a inserir no cursor: sempre a seguinte da maior.
export function proximaVariavel(texto = '') {
  const ns = variaveisDe(texto);
  return `{{${ns.length ? Math.max(...ns) + 1 : 1}}}`;
}

const contarEmojis = texto => (texto.match(EMOJI) || []).length;

const problema = (nivel, chave, campo, params = {}) => ({
  nivel,
  chave,
  campo,
  params,
});

function checarVariaveis(texto, exemplos, campo) {
  const lista = [];
  const ns = variaveisDe(texto);
  if (!ns.length) return lista;

  const ordenados = [...ns].sort((a, b) => a - b);
  if (ordenados.some((n, i) => n !== i + 1)) {
    lista.push(problema('erro', 'VAR_SEQUENCIA', campo));
  }
  if (/\}\}\s*\{\{/.test(texto)) {
    lista.push(problema('erro', 'VAR_COLADAS', campo));
  }
  const limpo = texto.trim();
  if (/^\{\{\s*\d+\s*\}\}/.test(limpo) || /\{\{\s*\d+\s*\}\}$/.test(limpo)) {
    lista.push(problema('erro', 'VAR_PONTAS', campo));
  }
  const faltam = ordenados.filter(n => !(exemplos[n - 1] || '').trim());
  if (faltam.length) {
    lista.push(
      problema('erro', 'EXEMPLO_FALTA', `${campo}-exemplos`, {
        variaveis: faltam.map(n => `{{${n}}}`).join(', '),
      })
    );
  }
  // A Meta recusa corpo "só de variáveis": pouca frase para muito dado.
  const palavras = limpo.replace(VARIAVEL, '').split(/\s+/).filter(Boolean);
  if (campo === 'corpo' && palavras.length < ns.length * 3) {
    lista.push(problema('aviso', 'VAR_DENSA', campo));
  }
  return lista;
}

function checarCabecalho(cab) {
  const lista = [];
  if (cab.tipo === 'TEXT') {
    if (!cab.texto.trim())
      lista.push(problema('erro', 'CAB_VAZIO', 'cabecalho'));
    if (cab.texto.length > LIMITES.cabecalho) {
      lista.push(
        problema('erro', 'CAB_LONGO', 'cabecalho', { max: LIMITES.cabecalho })
      );
    }
    const ns = variaveisDe(cab.texto);
    if (ns.length > 1)
      lista.push(problema('erro', 'CAB_VARIAVEIS', 'cabecalho'));
    if (ns.length === 1 && !cab.exemplo.trim()) {
      lista.push(
        problema('erro', 'EXEMPLO_FALTA', 'cabecalho-exemplos', {
          variaveis: '{{1}}',
        })
      );
    }
  } else if (cab.tipo !== 'NONE' && !cab.arquivo) {
    lista.push(problema('erro', 'CAB_MIDIA', 'cabecalho'));
  }
  return lista;
}

function checarBotoes(botoes) {
  const lista = [];
  if (botoes.length > LIMITES.botoes) {
    lista.push(
      problema('erro', 'BOTOES_MAX', 'botoes', { max: LIMITES.botoes })
    );
  }
  botoes.forEach((b, i) => {
    const campo = `botao-${i}`;
    if (!b.texto.trim()) lista.push(problema('erro', 'BOTAO_VAZIO', campo));
    if (b.texto.length > LIMITES.botao) {
      lista.push(
        problema('erro', 'BOTAO_LONGO', campo, { max: LIMITES.botao })
      );
    }
    if (
      b.tipo === 'URL' &&
      !/^https:\/\/[^\s.]+\.[^\s]+$/i.test(b.url.trim())
    ) {
      lista.push(problema('erro', 'BOTAO_URL', campo));
    }
    if (
      b.tipo === 'PHONE_NUMBER' &&
      b.telefone.replace(/\D/g, '').length < 12
    ) {
      lista.push(problema('erro', 'BOTAO_TELEFONE', campo));
    }
  });
  return lista;
}

// Tudo o que a Meta recusaria (erro: trava o envio) e o que costuma dar
// recusa ou custo maior (aviso: a pessoa decide). Vazio = pode enviar.
export function checar(m) {
  const lista = [];
  if (!m.nome.trim()) lista.push(problema('erro', 'NOME_VAZIO', 'nome'));
  else if (!/^[a-z0-9_]+$/.test(m.nome)) {
    lista.push(problema('erro', 'NOME_FORMATO', 'nome'));
  }

  lista.push(...checarCabecalho(m.cabecalho));

  if (!m.corpo.trim()) lista.push(problema('erro', 'CORPO_VAZIO', 'corpo'));
  if (m.corpo.length > LIMITES.corpo) {
    lista.push(
      problema('erro', 'CORPO_LONGO', 'corpo', { max: LIMITES.corpo })
    );
  }
  lista.push(...checarVariaveis(m.corpo, m.exemplos, 'corpo'));

  if (m.rodape.length > LIMITES.rodape) {
    lista.push(
      problema('erro', 'RODAPE_LONGO', 'rodape', { max: LIMITES.rodape })
    );
  }
  if (variaveisDe(m.rodape).length) {
    lista.push(problema('erro', 'RODAPE_VARIAVEL', 'rodape'));
  }

  lista.push(...checarBotoes(m.botoes));

  const tudo = [
    m.cabecalho.texto,
    m.corpo,
    m.rodape,
    ...m.botoes.map(b => b.url),
  ].join(' ');
  if (ENCURTADORES.test(tudo))
    lista.push(problema('erro', 'LINK_CURTO', 'corpo'));
  if (contarEmojis(m.corpo) > LIMITES.emojis) {
    lista.push(problema('aviso', 'EMOJIS', 'corpo', { max: LIMITES.emojis }));
  }
  if (PROMESSA.test(`${m.cabecalho.texto} ${m.corpo}`)) {
    lista.push(problema('aviso', 'PROMESSA', 'corpo'));
  }
  if (m.categoria === 'MARKETING') {
    lista.push(problema('aviso', 'MARKETING_OAB', 'categoria'));
  }
  return lista;
}

export const temErro = lista => lista.some(p => p.nivel === 'erro');

// ── A prévia: como chega no celular ─────────────────────────────────────────

const escapar = texto =>
  texto
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// A formatação do WhatsApp: *negrito*, _itálico_, ~riscado~, ```mono```.
// Escapa ANTES de marcar: o texto é de quem digita e vai para v-html.
export function formatarComoWhatsapp(texto = '', exemplos = []) {
  let html = escapar(texto);
  html = html.replace(VARIAVEL, (_, n) => {
    const valor = (exemplos[Number(n) - 1] || '').trim();
    return valor
      ? `<span class="mdl-var">${escapar(valor)}</span>`
      : `<span class="mdl-var mdl-var--vazia">{{${n}}}</span>`;
  });
  html = html
    .replace(/```([^`]+)```/g, '<code>$1</code>')
    .replace(
      /(^|[\s>])\*([^*\n]+)\*(?=[\s<.,!?;:]|$)/g,
      '$1<strong>$2</strong>'
    )
    .replace(/(^|[\s>])_([^_\n]+)_(?=[\s<.,!?;:]|$)/g, '$1<em>$2</em>')
    .replace(/(^|[\s>])~([^~\n]+)~(?=[\s<.,!?;:]|$)/g, '$1<s>$2</s>')
    .replace(/\n/g, '<br>');
  return html;
}

// Envolve a seleção com a marca de formatação (ou insere a marca vazia).
export function envolver(texto, inicio, fim, marca) {
  const antes = texto.slice(0, inicio);
  const meio = texto.slice(inicio, fim);
  const depois = texto.slice(fim);
  return {
    texto: `${antes}${marca}${meio}${marca}${depois}`,
    cursor: inicio + marca.length + meio.length,
  };
}

// ── Ida e volta com a Meta ──────────────────────────────────────────────────

const lerExemplos = (m, n) =>
  Array.from({ length: n }, (_, i) => (m.exemplos[i] || '').trim());

// O corpo que vai para análise, no formato da Meta (o mesmo que o portal da
// WAME monta). `handle`: a mídia de exemplo do cabeçalho já enviada.
export function montarComponentes(m, handle = null) {
  const componentes = [];
  const cab = m.cabecalho;
  if (cab.tipo === 'TEXT') {
    const c = { type: 'HEADER', format: 'TEXT', text: cab.texto.trim() };
    if (variaveisDe(cab.texto).length)
      c.example = { header_text: [cab.exemplo.trim()] };
    componentes.push(c);
  } else if (cab.tipo !== 'NONE' && handle) {
    componentes.push({
      type: 'HEADER',
      format: cab.tipo,
      example: { header_handle: [handle] },
    });
  }

  const corpo = { type: 'BODY', text: m.corpo.trim() };
  const n = variaveisDe(m.corpo).length;
  if (n) corpo.example = { body_text: [lerExemplos(m, n)] };
  componentes.push(corpo);

  if (m.rodape.trim())
    componentes.push({ type: 'FOOTER', text: m.rodape.trim() });

  if (m.botoes.length) {
    componentes.push({
      type: 'BUTTONS',
      buttons: m.botoes.map(b => {
        if (b.tipo === 'URL')
          return { type: 'URL', text: b.texto.trim(), url: b.url.trim() };
        if (b.tipo === 'PHONE_NUMBER') {
          return {
            type: 'PHONE_NUMBER',
            text: b.texto.trim(),
            phone_number: `+${b.telefone.replace(/\D/g, '')}`,
          };
        }
        return { type: 'QUICK_REPLY', text: b.texto.trim() };
      }),
    });
  }
  return componentes;
}

export const paraEnviar = (m, handle = null) => ({
  name: m.nome,
  language: m.idioma,
  category: m.categoria,
  components: montarComponentes(m, handle),
});

// Um modelo que veio da Meta, de volta para o formulário (editar).
export function doModeloDaMeta(t = {}) {
  const m = modeloVazio();
  const partes = t.components || [];
  const achar = tipo => partes.find(c => (c.type || '').toUpperCase() === tipo);
  m.id = t.id || null;
  m.nome = t.name || '';
  m.idioma = t.language || 'pt_BR';
  m.categoria = t.category || 'UTILITY';

  const cab = achar('HEADER');
  if (cab) {
    m.cabecalho.tipo = (cab.format || 'TEXT').toUpperCase();
    m.cabecalho.texto = cab.text || '';
    m.cabecalho.exemplo = cab.example?.header_text?.[0] || '';
    // A mídia de exemplo não volta da Meta como arquivo: fica marcada como
    // já enviada; trocar o arquivo pede um novo.
    if (m.cabecalho.tipo !== 'TEXT') {
      m.cabecalho.arquivo = {
        jaEnviado: true,
        handle: cab.example?.header_handle?.[0] || null,
      };
    }
  }
  const corpo = achar('BODY');
  m.corpo = corpo?.text || '';
  m.exemplos = [...(corpo?.example?.body_text?.[0] || [])];
  m.rodape = achar('FOOTER')?.text || '';
  m.botoes = (achar('BUTTONS')?.buttons || []).map(b => ({
    tipo: b.type,
    texto: b.text || '',
    url: b.url || '',
    telefone: (b.phone_number || '').replace(/\D/g, ''),
  }));
  return m;
}

// O texto do corpo de um modelo da lista (para a prévia de uma linha).
export const corpoDe = t =>
  (t?.components || []).find(c => (c.type || '').toUpperCase() === 'BODY')
    ?.text || '';
